import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { getJSON, patchJSON } from '@/lib/api'
import { apiErrorMessage, selectedPairContacts } from '@/pages/DuplicateReviewPage.helpers'
import {
  ComparisonCard,
  MergeDialog,
  ReviewPlaceholder,
  SuggestionList,
} from '@/pages/DuplicateReviewPage.parts'
import type {
  ContactResponse,
  DuplicateSuggestion,
  DuplicateSuggestionContact,
  DuplicateSuggestionResponse,
  DuplicateSuggestionsResponse,
  SelectedDuplicatePair,
  SuggestionListState,
} from '@/pages/DuplicateReviewPage.types'

interface ReviewModel {
  suggestions: DuplicateSuggestion[]
  selectedSuggestion: DuplicateSuggestion | null
  selectedPair: SelectedDuplicatePair | null
  selectedSuggestionId: number | null
  suggestionState: SuggestionListState
  mergeOpen: boolean
  mergeConfirmation: string
  dismissError: Error | null
  dismissPending: boolean
  mergeError: Error | null
  mergePending: boolean
  setSurvivorId: (contactId: number) => void
  selectSuggestion: (suggestion: DuplicateSuggestion) => void
  dismissSelected: () => void
  openMergeDialog: () => void
  setMergeOpen: (open: boolean) => void
  setMergeConfirmation: (confirmation: string) => void
  mergeSelected: () => void
}

export function DuplicateReviewPage() {
  const review = useDuplicateReviewModel()
  const hasSuggestions = review.suggestions.length > 0

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 sm:px-8 sm:py-8">
      <DuplicateReviewHeader pairCount={review.suggestions.length} />
      {hasSuggestions ? (
        <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <SuggestionList
            suggestions={review.suggestions}
            selectedSuggestionId={review.selectedSuggestionId}
            state={review.suggestionState}
            onSelect={review.selectSuggestion}
          />
          <ReviewPane review={review} />
        </div>
      ) : (
        <ReviewPlaceholder state={review.suggestionState} />
      )}
    </div>
  )
}

function DuplicateReviewHeader({ pairCount }: { pairCount: number }) {
  return (
    <header className="flex flex-col gap-1 pb-5">
      <h1 className="text-heading-sm font-normal text-foreground">Duplicate suggestions</h1>
      <p className="text-body-sm text-muted-foreground">
        {pairCount === 1 ? '1 pair awaiting review' : `${pairCount} pairs awaiting review`}
      </p>
    </header>
  )
}

function ReviewPane({ review }: { review: ReviewModel }) {
  return (
    <div className="min-w-0">
      {review.dismissError && (
        <Alert className="mb-4">{apiErrorMessage(review.dismissError)}</Alert>
      )}
      {review.mergeError && !review.mergeOpen && (
        <Alert className="mb-4">{apiErrorMessage(review.mergeError)}</Alert>
      )}
      {review.selectedSuggestion && review.selectedPair ? (
        <SelectedSuggestionPane review={review} />
      ) : (
        <ReviewPlaceholder state={review.suggestionState} />
      )}
    </div>
  )
}

function SelectedSuggestionPane({ review }: { review: ReviewModel }) {
  const pair = review.selectedPair
  if (!pair || !review.selectedSuggestion) return null

  return (
    <>
      <ComparisonCard
        suggestion={review.selectedSuggestion}
        survivorId={pair.survivor.id}
        onSurvivorChange={review.setSurvivorId}
      />
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={review.dismissPending}
          onClick={review.dismissSelected}
        >
          Dismiss
        </Button>
        <Button type="button" disabled={review.mergePending} onClick={review.openMergeDialog}>
          Merge contacts
        </Button>
      </div>
      <MergeDialog
        open={review.mergeOpen}
        pair={pair}
        confirmation={review.mergeConfirmation}
        error={review.mergeError}
        pending={review.mergePending}
        onConfirmationChange={review.setMergeConfirmation}
        onMerge={review.mergeSelected}
        onOpenChange={review.setMergeOpen}
      />
    </>
  )
}

function useDuplicateReviewModel(): ReviewModel {
  const queryClient = useQueryClient()
  const [selectedSuggestionId, setSelectedSuggestionId] = React.useState<number | null>(null)
  const [survivorId, setSurvivorId] = React.useState<number | null>(null)
  const [mergeOpen, setMergeOpen] = React.useState(false)
  const [mergeConfirmation, setMergeConfirmation] = React.useState('')
  const suggestionsQuery = useQuery({
    queryKey: ['duplicate-suggestions'],
    queryFn: () => getJSON<DuplicateSuggestionsResponse>('/api/v1/duplicate_suggestions'),
    retry: false,
  })
  const suggestions = React.useMemo(
    () => suggestionsQuery.data?.duplicate_suggestions ?? [],
    [suggestionsQuery.data],
  )
  const selectedSuggestion =
    suggestions.find((suggestion) => suggestion.id === selectedSuggestionId) ?? null
  const selectedPair = selectedSuggestion
    ? selectedPairContacts(selectedSuggestion, survivorId)
    : null
  const dismissSuggestion = useDismissSuggestion(queryClient)
  const mergeContacts = useMergeContacts(queryClient, closeMergeDialog)

  useDefaultSelectedSuggestion(
    suggestions,
    selectedSuggestionId,
    suggestionsQuery.isSuccess,
    setSelectedSuggestionId,
  )
  useDefaultSurvivor(selectedSuggestion, survivorId, setSurvivorId)

  function closeMergeDialog() {
    setMergeOpen(false)
    setMergeConfirmation('')
  }

  function selectSuggestion(suggestion: DuplicateSuggestion) {
    dismissSuggestion.reset()
    mergeContacts.reset()
    setSelectedSuggestionId(suggestion.id)
    setSurvivorId(suggestion.contact_one.id)
  }

  return {
    suggestions,
    selectedSuggestion,
    selectedPair,
    selectedSuggestionId,
    suggestionState: suggestionState(suggestionsQuery.isLoading, suggestionsQuery.isError),
    mergeOpen,
    mergeConfirmation,
    dismissError: dismissSuggestion.error,
    dismissPending: dismissSuggestion.isPending,
    mergeError: mergeContacts.error,
    mergePending: mergeContacts.isPending,
    setSurvivorId,
    selectSuggestion,
    dismissSelected: () => selectedSuggestion && dismissSuggestion.mutate(selectedSuggestion),
    openMergeDialog: () => {
      mergeContacts.reset()
      setMergeConfirmation('')
      setMergeOpen(true)
    },
    setMergeOpen,
    setMergeConfirmation,
    mergeSelected: () => {
      if (!selectedPair) return
      mergeContacts.mutate({ ...selectedPair, confirmation: mergeConfirmation })
    },
  }
}

function useDismissSuggestion(queryClient: ReturnType<typeof useQueryClient>) {
  return useMutation({
    mutationFn: (suggestion: DuplicateSuggestion) =>
      patchJSON<DuplicateSuggestionResponse>(
        `/api/v1/duplicate_suggestions/${suggestion.id}/dismiss`,
        {},
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['duplicate-suggestions'] })
    },
  })
}

function useMergeContacts(queryClient: ReturnType<typeof useQueryClient>, onMerged: () => void) {
  return useMutation({
    mutationFn: mergeContacts,
    onSuccess: async () => {
      onMerged()
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['duplicate-suggestions'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
      ])
    },
  })
}

function useDefaultSelectedSuggestion(
  suggestions: DuplicateSuggestion[],
  selectedSuggestionId: number | null,
  loaded: boolean,
  onSelect: (id: number | null) => void,
) {
  React.useEffect(() => {
    if (!loaded) return
    onSelect(defaultSuggestionId(suggestions, selectedSuggestionId))
  }, [loaded, onSelect, selectedSuggestionId, suggestions])
}

function useDefaultSurvivor(
  selectedSuggestion: DuplicateSuggestion | null,
  survivorId: number | null,
  onSelect: (id: number | null) => void,
) {
  React.useEffect(() => {
    onSelect(defaultSurvivorId(selectedSuggestion, survivorId))
  }, [onSelect, selectedSuggestion, survivorId])
}

function defaultSuggestionId(
  suggestions: DuplicateSuggestion[],
  selectedSuggestionId: number | null,
) {
  if (suggestions.length === 0) return null
  if (
    selectedSuggestionId &&
    suggestions.some((suggestion) => suggestion.id === selectedSuggestionId)
  ) {
    return selectedSuggestionId
  }

  return suggestions[0].id
}

function defaultSurvivorId(
  selectedSuggestion: DuplicateSuggestion | null,
  survivorId: number | null,
) {
  if (!selectedSuggestion) return null

  const contactIds = [selectedSuggestion.contact_one.id, selectedSuggestion.contact_two.id]
  return survivorId && contactIds.includes(survivorId)
    ? survivorId
    : selectedSuggestion.contact_one.id
}

function suggestionState(loading: boolean, error: boolean): SuggestionListState {
  if (loading) return 'loading'
  if (error) return 'error'

  return 'ready'
}

function mergeContacts({
  merged,
  survivor,
  confirmation,
}: {
  merged: DuplicateSuggestionContact
  survivor: DuplicateSuggestionContact
  confirmation: string
}) {
  return patchJSON<ContactResponse>(`/api/v1/contacts/${merged.id}/merge`, {
    survivor_contact_id: survivor.id,
    confirmation,
  })
}
